import * as analytics from 'analytics/Analytics';
import BenchlingConfig from 'BenchlingConfig';
import {COLLABORATION_ANALYTICS_ACTIONS} from 'constants/analytics-products';
import SubtemplateActions from 'notebook/entry-templates/SubtemplateActions';
import SubtemplateStore from 'notebook/entry-templates/SubtemplateStore';
import TemplateActions from 'notebook/entry-templates/TemplateActions';
import TemplateStore from 'notebook/entry-templates/TemplateStore';
import {RealtimeMessageType} from 'realtime/Constants';
import {getCollaborationAnalyticsMetadata} from 'realtime/util/analytics/CollaborationAnalyticsMetadata';
import {isCurrentClient} from 'realtime/util/RealtimeUtil';
import APIUtil from 'util/APIUtil';

import type {DocumentUpdateMessageHandler} from './DocumentUpdateMessageHandlerConstants';

type PublishableStoreAndActions =
  | [typeof TemplateStore, typeof TemplateActions]
  | [typeof SubtemplateStore, typeof SubtemplateActions];

function getPublishableStoreAndActions(fileId: string): PublishableStoreAndActions | null {
  if (!BenchlingConfig.ELN_REALTIME_TEMPLATE_UPDATES) {
    return null;
  }
  if (APIUtil.isEntryTemplate(fileId)) {
    return [TemplateStore, TemplateActions];
  } else if (APIUtil.isSubtemplate(fileId)) {
    return [SubtemplateStore, SubtemplateActions];
  }
  return null;
}

export const publishingUpdateMessageHandler: DocumentUpdateMessageHandler<
  typeof RealtimeMessageType.PUBLISHING_UPDATE
> = {
  active: (fileId, {clientId}) => {
    const storeAndActions = getPublishableStoreAndActions(fileId);
    if (storeAndActions == null || !clientId || isCurrentClient(clientId)) {
      return;
    }
    const [Store, Actions] = storeAndActions;

    Actions.refreshPublishingState(fileId);
    analytics.trackEvent(
      COLLABORATION_ANALYTICS_ACTIONS.RECEIVED_REALTIME_UPDATE,
      getCollaborationAnalyticsMetadata(fileId)
    );

    const editorState = Store.getEditorState(fileId);
    if (editorState != null) {
      // Don't update the item if the client is viewing an old version, but refresh the history so it
      // lists the new version
      if (editorState.openVersionId !== editorState.metadata?.latestVersionId) {
        Actions.fetchVersions(fileId);
        return;
      }
      // Don't update the item if the current user has started editing
      if (Store.hasUnsavedChanges(fileId)) {
        return;
      }
    }
    Actions.open(fileId, {});
  },
  inactive: (fileId, {clientId}) => {
    const storeAndActions = getPublishableStoreAndActions(fileId);
    if (storeAndActions == null || !clientId || isCurrentClient(clientId)) {
      return;
    }
    const [Store, Actions] = storeAndActions;

    Actions.refreshPublishingState(fileId);
    if (Store.inStore(fileId)) {
      Actions.postServerUpdate(fileId, {isStale: true});
    }
  },
};
